import * as THREE from "three";
import { useEffect, useRef } from "react";

const DESKTOP_BLADE_COUNT = 36000;
const MOBILE_BLADE_COUNT = 22000;

function seededRandom(seed: number) {
  let state = seed >>> 0;
  return () => {
    state = (state * 1664525 + 1013904223) >>> 0;
    return state / 4294967296;
  };
}

function createGrassGeometry(bladeCount: number) {
  const geometry = new THREE.InstancedBufferGeometry();
  const positions = new Float32Array([
    -0.5, 0.0, 0.0, 0.5, 0.0, 0.0,
    -0.43, 0.28, 0.0, 0.43, 0.28, 0.0,
    -0.32, 0.56, 0.0, 0.32, 0.56, 0.0,
    -0.18, 0.82, 0.0, 0.18, 0.82, 0.0,
    0.0, 1.0, 0.0,
  ]);
  const indices = [
    0, 1, 2, 1, 3, 2,
    2, 3, 4, 3, 5, 4,
    4, 5, 6, 5, 7, 6,
    6, 7, 8,
  ];
  geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  geometry.setIndex(indices);

  const offsets = new Float32Array(bladeCount * 3);
  const parameters = new Float32Array(bladeCount * 4);
  const colors = new Float32Array(bladeCount * 3);
  const random = seededRandom(72450674);

  for (let i = 0; i < bladeCount; i += 1) {
    const depth = Math.pow(random(), 0.82);
    const z = 1.8 - depth * 9.4;
    const widthAtDepth = 7.8 + depth * 4.2;
    const offsetIndex = i * 3;
    const parameterIndex = i * 4;
    const colorIndex = i * 3;
    const x = (random() - 0.5) * widthAtDepth * 2;

    offsets[offsetIndex] = x;
    offsets[offsetIndex + 1] = -0.9 + (random() - 0.5) * 0.035;
    offsets[offsetIndex + 2] = z;

    parameters[parameterIndex] = random() * Math.PI * 2;
    parameters[parameterIndex + 1] = 0.78 + random() * 1.02;
    parameters[parameterIndex + 2] = 0.09 + random() * 0.14;
    parameters[parameterIndex + 3] = random() * Math.PI * 2;

    const patch = Math.max(0, Math.min(1, 0.48 + Math.sin(x * 0.72 + z * 0.43) * 0.2 + (random() - 0.5) * 0.42));
    const warm = random();
    colors[colorIndex] = 0.56 + patch * 0.1 + warm * 0.015;
    colors[colorIndex + 1] = 0.7 + patch * 0.12;
    colors[colorIndex + 2] = 0.045 + patch * 0.045 + warm * 0.008;
  }

  geometry.setAttribute("instanceOffset", new THREE.InstancedBufferAttribute(offsets, 3));
  geometry.setAttribute("instanceParameters", new THREE.InstancedBufferAttribute(parameters, 4));
  geometry.setAttribute("instanceColor", new THREE.InstancedBufferAttribute(colors, 3));
  geometry.instanceCount = bladeCount;
  geometry.computeBoundingSphere();
  return geometry;
}

// Independent Three.js implementation inspired by:
// https://github.com/kseniya7991/fur-shader
const vertexShader = `
  attribute vec3 instanceOffset;
  attribute vec4 instanceParameters;
  attribute vec3 instanceColor;

  uniform float uTime;

  varying float vBladeHeight;
  varying vec3 vColor;
  varying float vDepth;
  varying float vBladeLight;

  void main() {
    float yaw = instanceParameters.x;
    float bladeHeight = instanceParameters.y;
    float bladeWidth = instanceParameters.z;
    float phase = instanceParameters.w;
    float heightFactor = position.y;

    vec2 fieldPosition = instanceOffset.xz;
    float gustA = pow(max(0.0,
      sin(fieldPosition.x * 0.72 - uTime * 0.94)
      * cos(fieldPosition.y * 0.51 + uTime * 0.34)), 2.0);
    float gustB = pow(max(0.0,
      sin(fieldPosition.x * 0.38 + fieldPosition.y * 0.82 + uTime * 0.71)
      * cos(fieldPosition.x * 0.55 - fieldPosition.y * 0.36 - uTime * 0.43)), 2.0);
    float gustC = pow(max(0.0,
      cos(fieldPosition.x * 0.83 - fieldPosition.y * 0.29 - uTime * 0.58)
      * sin(fieldPosition.y * 0.67 + uTime * 0.47)), 2.0);
    float gustD = pow(max(0.0,
      sin(fieldPosition.x * 0.46 - fieldPosition.y * 0.74 + uTime * 0.39)
      * sin(fieldPosition.x * 0.69 + fieldPosition.y * 0.31 - uTime * 0.76)), 2.0);
    float gustE = pow(max(0.0,
      cos(fieldPosition.x * 0.91 + fieldPosition.y * 0.57 + uTime * 0.63)
      * cos(fieldPosition.x * 0.34 - fieldPosition.y * 0.88 - uTime * 0.29)), 2.0);

    float driftA = sin(uTime * 0.17 + 0.4) * 0.24;
    float driftB = sin(uTime * 0.13 + 2.1) * 0.3;
    float driftC = sin(uTime * 0.19 + 4.2) * 0.21;
    float driftD = sin(uTime * 0.11 + 1.2) * 0.27;
    float driftE = sin(uTime * 0.23 + 3.4) * 0.19;
    vec2 directionA = mat2(cos(driftA), -sin(driftA), sin(driftA), cos(driftA)) * normalize(vec2(1.0, 0.18));
    vec2 directionB = mat2(cos(driftB), -sin(driftB), sin(driftB), cos(driftB)) * normalize(vec2(-1.0, 0.38));
    vec2 directionC = mat2(cos(driftC), -sin(driftC), sin(driftC), cos(driftC)) * normalize(vec2(0.16, -1.0));
    vec2 directionD = mat2(cos(driftD), -sin(driftD), sin(driftD), cos(driftD)) * normalize(vec2(0.76, 0.65));
    vec2 directionE = mat2(cos(driftE), -sin(driftE), sin(driftE), cos(driftE)) * normalize(vec2(-0.42, -1.0));
    float strengthA = 0.48 + (sin(uTime * 0.29 + 0.2) * 0.5 + 0.5) * 0.72;
    float strengthB = 0.42 + (sin(uTime * 0.37 + 1.8) * 0.5 + 0.5) * 0.8;
    float strengthC = 0.5 + (sin(uTime * 0.23 + 3.7) * 0.5 + 0.5) * 0.68;
    float strengthD = 0.4 + (sin(uTime * 0.33 + 5.1) * 0.5 + 0.5) * 0.86;
    float strengthE = 0.46 + (sin(uTime * 0.41 + 2.9) * 0.5 + 0.5) * 0.74;
    float eddyAngle = sin(fieldPosition.x * 0.64 + fieldPosition.y * 0.2 + uTime * 0.24) * 2.4
      + cos(fieldPosition.y * 0.73 - fieldPosition.x * 0.17 - uTime * 0.2) * 1.8;
    float eddyPulse = smoothstep(0.42, 0.9,
      sin(fieldPosition.x * 0.96 - fieldPosition.y * 0.62 + uTime * 1.04) * 0.5 + 0.5);
    vec2 localEddy = vec2(cos(eddyAngle), sin(eddyAngle)) * eddyPulse * 0.12;
    vec2 windVector = directionA * gustA * 0.22 * strengthA
      + directionB * gustB * 0.2 * strengthB
      + directionC * gustC * 0.18 * strengthC
      + directionD * gustD * 0.17 * strengthD
      + directionE * gustE * 0.16 * strengthE
      + localEddy;
    float tipInfluence = pow(heightFactor, 1.45);

    float buriedBase = (1.0 - heightFactor) * 0.55;
    vec3 localPosition = vec3(position.x * bladeWidth, heightFactor * bladeHeight - buriedBase, 0.0);
    float cosine = cos(yaw);
    float sine = sin(yaw);
    localPosition.xz = mat2(cosine, -sine, sine, cosine) * localPosition.xz;

    vec3 worldPosition = instanceOffset + localPosition;
    worldPosition.xz += windVector * tipInfluence;
    worldPosition.y -= length(windVector) * tipInfluence * 0.075;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(worldPosition, 1.0);

    vBladeHeight = heightFactor;
    vColor = instanceColor;
    vDepth = clamp((-instanceOffset.z + 1.0) / 9.4, 0.0, 1.0);
    vBladeLight = 0.92 + (cos(yaw + 1.1) * 0.5 + 0.5) * 0.12;
  }
`;

const fragmentShader = `
  precision highp float;

  varying float vBladeHeight;
  varying vec3 vColor;
  varying float vDepth;
  varying float vBladeLight;

  void main() {
    vec3 color = mix(vColor * 0.52, vColor * 1.16, smoothstep(0.04, 0.96, vBladeHeight));
    color *= mix(1.0, 0.9, vDepth) * vBladeLight;
    gl_FragColor = vec4(color, 1.0);
  }
`;

export function FurGrass() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true, premultipliedAlpha: true });
    renderer.setClearColor(0x000000, 0);
    renderer.outputColorSpace = THREE.SRGBColorSpace;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(29, 1, 0.1, 50);
    camera.position.set(0, 6.15, 6.25);
    camera.lookAt(0, -0.62, -2.85);

    const geometry = createGrassGeometry(window.innerWidth < 700 ? MOBILE_BLADE_COUNT : DESKTOP_BLADE_COUNT);
    const material = new THREE.ShaderMaterial({
      vertexShader,
      fragmentShader,
      transparent: false,
      depthWrite: true,
      side: THREE.DoubleSide,
      uniforms: { uTime: { value: 0 } },
    });
    const field = new THREE.Mesh(geometry, material);
    field.frustumCulled = false;
    scene.add(field);

    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let visible = false;
    let animationFrame = 0;
    let lastFrame = 0;

    const resize = () => {
      const width = Math.max(1, canvas.clientWidth);
      const height = Math.max(1, canvas.clientHeight);
      const isMobile = width < 700;
      const pixelRatioCap = isMobile ? 1.5 : 2;
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, pixelRatioCap));
      renderer.setSize(width, height, false);
      camera.aspect = width / height;
      camera.fov = isMobile ? 46 : 31;
      field.position.z = isMobile ? -1.15 : 0;
      field.scale.y = isMobile ? 2.7 : 1;
      camera.updateProjectionMatrix();
    };

    const render = (now: number) => {
      if (!visible) return;
      if (now - lastFrame < 32) {
        animationFrame = requestAnimationFrame(render);
        return;
      }
      lastFrame = now;
      material.uniforms.uTime.value = reducedMotion ? 0 : now * 0.00072;
      renderer.render(scene, camera);
      if (!reducedMotion) animationFrame = requestAnimationFrame(render);
    };

    const scheduleRender = () => {
      cancelAnimationFrame(animationFrame);
      animationFrame = requestAnimationFrame(render);
    };
    const intersection = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      if (visible) scheduleRender();
    }, { rootMargin: "180px" });
    const resizeObserver = new ResizeObserver(() => {
      resize();
      if (visible) scheduleRender();
    });

    resize();
    intersection.observe(canvas);
    resizeObserver.observe(canvas);

    return () => {
      intersection.disconnect();
      resizeObserver.disconnect();
      cancelAnimationFrame(animationFrame);
      geometry.dispose();
      material.dispose();
      renderer.dispose();
    };
  }, []);

  return <canvas ref={canvasRef} className="grass-shader" aria-hidden="true" />;
}
